import React, { useState } from 'react'
import { CurrencyInput } from '@/components/ui'
import { useTranslation } from '@/contexts/TranslationContext'
import { matchesSearch } from '@/lib/search'
import type { Party } from '@/lib/services/billing'

export interface EntryFormInput {
  description: string
  amount: string
  due_date: string
  party_name_snapshot: string
  party_id?: string | null
  party_whatsapp_snapshot: string
  party_pix_key_snapshot: string
  payment_method_snapshot: string
  notes: string
  paid_on_launch: boolean
}

export const EntryForm = ({
  formData,
  setFormData,
  formErrors,
  submitting,
  editingEntity,
  mode,
  parties,
  paymentMethodOptions,
  setConfirmSubmitOpen,
}: {
  formData: EntryFormInput
  setFormData: (d: EntryFormInput) => void
  formErrors: Record<string, string>
  submitting: boolean
  editingEntity: any | null
  mode: 'create' | 'edit' | 'view'
  parties: Party[]
  paymentMethodOptions: Array<{ value: string; label: string }>
  setConfirmSubmitOpen: (v: boolean) => void
}) => {
  const { t, locale } = useTranslation()
  const te = (key: string, params?: Record<string, string>) => t(`billing.entries.${key}`, params)
  const disabled = submitting || mode === 'view'
  const field = (key: string) => `billing.entries.record.fields.${key}`
  const [partyNameFocused, setPartyNameFocused] = useState(false)

  const filteredParties = (!formData.party_id && (formData.party_name_snapshot || '').trim().length > 0)
    ? parties.filter(p => matchesSearch(p.name, (formData.party_name_snapshot || '').trim())).slice(0, 6)
    : []

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          {t(field('description'))} <span className="text-rose-500">*</span>
        </label>
        <input
          type="text"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder={t(field('descriptionPlaceholder'))}
          disabled={disabled}
          className={`w-full px-4 py-2 rounded-lg border ${
            formErrors.description ? 'border-red-300' : 'border-slate-300'
          } dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500`}
          autoFocus
        />
        {formErrors.description && <p className="mt-1 text-sm text-red-600">{formErrors.description}</p>}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            {t(field('amount'))} <span className="text-rose-500">*</span>
          </label>
          <div className="relative">
            <CurrencyInput
              value={formData.amount}
              onChange={(v) => setFormData({ ...formData, amount: v })}
              locale={locale}
              error={!!formErrors.amount}
              disabled={disabled}
              className="pr-10"
            />
            {!disabled && formData.amount && (
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setFormData({ ...formData, amount: '' })}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors z-10"
                title={t('billing.actions.clear') || 'Limpar valor'}
                aria-label={t('billing.actions.clear') || 'Limpar valor'}
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          {formErrors.amount && <p className="mt-1 text-sm text-red-600">{formErrors.amount}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            {t(field('dueDate'))} <span className="text-rose-500">*</span>
          </label>
          <input
            type="date"
            lang={locale}
            value={formData.due_date}
            onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
            disabled={disabled}
            className={`w-full px-4 py-2 rounded-lg border ${
              formErrors.due_date ? 'border-red-300' : 'border-slate-300'
            } dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500`}
          />
          {formErrors.due_date && <p className="mt-1 text-sm text-red-600">{formErrors.due_date}</p>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="relative">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            {t(field('partyName'))}
          </label>
          <input
            type="text"
            value={formData.party_name_snapshot}
            onChange={(e) => setFormData({ ...formData, party_name_snapshot: e.target.value, party_id: undefined })}
            onFocus={() => setPartyNameFocused(true)}
            onBlur={() => setTimeout(() => setPartyNameFocused(false), 150)}
            placeholder={t(field('partyNamePlaceholder'))}
            disabled={disabled}
            className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />

          {mode !== 'view' && partyNameFocused && filteredParties.length > 0 && (
            <div className="absolute z-50 left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-56 overflow-auto">
              {filteredParties.map(p => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setFormData({ ...formData, party_name_snapshot: p.name, party_whatsapp_snapshot: p.whatsapp ?? '', party_pix_key_snapshot: p.pix_key ?? '', party_id: p.id })}
                  className="w-full text-left px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-700"
                >
                  <div className="font-medium text-slate-900 dark:text-white">{p.name}</div>
                  {p.whatsapp && <div className="text-xs text-slate-500 dark:text-slate-400">{p.whatsapp}</div>}
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            {t(field('partyWhatsapp'))}
          </label>
          <input
            type="tel"
            value={formData.party_whatsapp_snapshot}
            onChange={(e) => setFormData({ ...formData, party_whatsapp_snapshot: e.target.value })}
            placeholder={t(field('partyWhatsappPlaceholder'))}
            disabled={disabled}
            className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          {t(field('paymentMethod'))}
        </label>
        <select
          value={formData.payment_method_snapshot}
          onChange={(e) => setFormData({ ...formData, payment_method_snapshot: e.target.value })}
          disabled={disabled}
          className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
        >
          <option value="">—</option>
          {paymentMethodOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
          <input
            type="checkbox"
            checked={!!formData.paid_on_launch}
            onChange={(e) => setFormData({ ...formData, paid_on_launch: e.target.checked })}
            disabled={disabled}
            className="h-4 w-4 rounded border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-emerald-600 focus:outline-none"
          />
          <span>{t(field('paidOnLaunch'))}</span>
        </label>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          {t(field('notes'))}
        </label>
        <textarea
          rows={3}
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return
            if (typeof window === 'undefined') return
            if (mode !== 'create') return
            if ((formData.notes || '').trim().length > 0) return

            e.preventDefault()

            const form = document.getElementById('entity-edit-form') as HTMLElement | null
            if (form) {
              const focusableSelector = 'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]):not([tabindex="-1"]), input:not([type="hidden"]):not([disabled]):not([tabindex="-1"]), select:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])'
              const all = Array.from(form.querySelectorAll<HTMLElement>(focusableSelector)).filter(el => (el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0))
              const cur = all.indexOf(e.currentTarget as HTMLElement)
              let next = cur === -1 ? 0 : cur + 1
              if (next >= all.length) next = 0
              try { all[next]?.focus() } catch (err) {}
            }

            setConfirmSubmitOpen(true)
          }}
          placeholder={t(field('notesPlaceholder'))}
          disabled={disabled}
          className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
        />
      </div>
    </div>
  )
}

export default EntryForm
